import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { SessionState } from '../types';

type SessionStore = SessionState & {
  toggleChallan: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  dismissNudge: (id: string) => void;
  resetDismissals: () => void;
  startPayment: () => void;
  completePayment: () => void;
  resetPayment: () => void;
  startRecharge: (source: string) => void;
  completeRecharge: () => void;
  abandonRecharge: () => void;
  resetRecharge: () => void;
  resetAll: () => void;
  setDemoStep: (step: number) => void;
  setDemoAutoPlay: (auto: boolean) => void;
};

const initialState: SessionState = {
  selectedChallanIds: [],
  dismissedNudgeIds: [],
  paymentStatus: 'idle',
  rechargeStatus: 'idle',
  lastRechargeSource: null,
  demoStep: 0,
  demoAutoPlay: true,
};

export const useSessionStore = create<SessionStore>()(
  immer((set) => ({
    ...initialState,

    toggleChallan: (id) =>
      set((state) => {
        const idx = state.selectedChallanIds.indexOf(id);
        if (idx === -1) {
          state.selectedChallanIds.push(id);
        } else {
          state.selectedChallanIds.splice(idx, 1);
        }
      }),

    selectAll: (ids) =>
      set((state) => {
        state.selectedChallanIds = ids;
      }),

    clearSelection: () =>
      set((state) => {
        state.selectedChallanIds = [];
      }),

    dismissNudge: (id) =>
      set((state) => {
        if (!state.dismissedNudgeIds.includes(id)) {
          state.dismissedNudgeIds.push(id);
        }
      }),

    resetDismissals: () =>
      set((state) => {
        state.dismissedNudgeIds = [];
      }),

    startPayment: () =>
      set((state) => {
        state.paymentStatus = 'started';
      }),

    completePayment: () =>
      set((state) => {
        state.paymentStatus = 'success';
      }),

    resetPayment: () =>
      set((state) => {
        state.paymentStatus = 'idle';
      }),

    startRecharge: (source) =>
      set((state) => {
        state.rechargeStatus = 'started';
        state.lastRechargeSource = source;
      }),

    completeRecharge: () =>
      set((state) => {
        state.rechargeStatus = 'completed';
      }),

    abandonRecharge: () =>
      set((state) => {
        state.rechargeStatus = 'abandoned';
      }),

    resetRecharge: () =>
      set((state) => {
        state.rechargeStatus = 'idle';
        state.lastRechargeSource = null;
      }),

    resetAll: () => set(initialState),

    setDemoStep: (step) =>
      set((state) => {
        state.demoStep = step;
      }),

    setDemoAutoPlay: (auto) =>
      set((state) => {
        state.demoAutoPlay = auto;
      }),
  }))
);
